import {
  deriveThreadExecutionPhase,
  emptyThreadPlacement,
  planPlacementReorder,
  type PlacementAssignment,
} from "@afar-research/afar";
import { scopedProjectKey, scopeProjectRef } from "@afar-research/client-runtime/environment";
import type { SidebarProjectSnapshot } from "../sidebarProjectGrouping";
import * as React from "react";
import { defaultAnimateLayoutChanges, type AnimateLayoutChanges } from "@dnd-kit/sortable";
import type {
  SidebarProjectSortOrder,
  SidebarThreadSortOrder,
} from "@afar-research/contracts/settings";
import type { ThreadManagement, ThreadPlacement } from "@afar-research/contracts";
import {
  getThreadSortTimestamp,
  sortThreads,
  toSortableTimestamp,
  type ThreadSortInput,
} from "../lib/threadSort";
import type { SidebarThreadSummary, Thread } from "../types";
import { cn } from "../lib/utils";
import { isLatestTurnSettled } from "../session-logic";
import {
  THREAD_HISTORY_CLIENT_WINDOW_MAX_DECODED_BYTES,
  THREAD_HISTORY_PREWARM_MAX_DECODED_BYTES,
  THREAD_HISTORY_PREWARM_MAX_THREADS,
} from "@afar-research/shared/threadHistory";

/** Footer destinations available before an Environment can serve the workspace. */
export function sidebarFooterDestination(
  to: "/settings" | "/pull-requests" | "/schedule" | "/usage",
  shellReady: boolean,
) {
  if (to === "/settings") return shellReady ? "/settings" : "/settings/connections";
  return shellReady ? to : null;
}

/** Uses synchronized threads, so collapsed or paginated projects stay visible. */
export function getSidebarProjectKeysWithThreads(
  projects: readonly Pick<SidebarProjectSnapshot, "projectKey" | "memberProjectRefs">[],
  threads: readonly Pick<SidebarThreadSummary, "environmentId" | "projectId" | "archivedAt">[],
): ReadonlySet<string> {
  const occupiedRefs = new Set(
    threads
      .filter((thread) => thread.archivedAt === null)
      .map((thread) => scopedProjectKey(scopeProjectRef(thread.environmentId, thread.projectId))),
  );
  return new Set(
    projects
      .filter((project) =>
        project.memberProjectRefs.some((ref) => occupiedRefs.has(scopedProjectKey(ref))),
      )
      .map((project) => project.projectKey),
  );
}

export const THREAD_SELECTION_SAFE_SELECTOR = "[data-thread-item], [data-thread-selection-safe]";
export const THREAD_JUMP_HINT_SHOW_DELAY_MS = 100;
// Visible sidebar rows are prewarmed into the thread-detail cache so opening a
// nearby thread usually reuses an already-hot subscription.
export const SIDEBAR_THREAD_PREWARM_LIMIT = THREAD_HISTORY_PREWARM_MAX_THREADS;
export const SIDEBAR_THREAD_PREWARM_DECODED_BYTE_LIMIT = THREAD_HISTORY_PREWARM_MAX_DECODED_BYTES;
export type SidebarNewThreadEnvMode = "local" | "worktree";
type SidebarProject = {
  id: string;
  title: string;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
};

export type ThreadTraversalDirection = "previous" | "next";

export function hasReadyProjectEnvironment(
  environments: ReadonlyArray<{ readonly connection: { readonly phase: string } }>,
): boolean {
  return environments.some((environment) => environment.connection.phase === "ready");
}

export function areAllProjectEnvironmentsUnavailable(
  members: ReadonlyArray<{ readonly environmentId: string }>,
  blockedEnvironmentIds: ReadonlySet<string>,
): boolean {
  return members.every((member) => blockedEnvironmentIds.has(member.environmentId));
}

export function resolveSidebarNewThreadProjectMember<
  TMember extends { readonly environmentId: string },
>(members: ReadonlyArray<TMember>, blockedEnvironmentIds: ReadonlySet<string>): TMember | null {
  return (
    members.find((member) => !blockedEnvironmentIds.has(member.environmentId)) ?? members[0] ?? null
  );
}

export interface ThreadStatusPill {
  label: "Working" | "Failed" | "Done" | "Approval" | "Input" | "Plan Ready";
  colorClass: string;
  dotClass: string;
}

const THREAD_STATUS_PRIORITY: Record<ThreadStatusPill["label"], number> = {
  Approval: 5,
  Input: 4,
  Working: 3,
  Failed: 4,
  "Plan Ready": 2,
  Done: 1,
};

type ThreadStatusInput = Pick<
  SidebarThreadSummary,
  | "hasActionableProposedPlan"
  | "hasPendingApprovals"
  | "hasPendingUserInput"
  | "interactionMode"
  | "latestTurn"
  | "session"
> & {
  lastVisitedAt?: string | undefined;
};

export interface ThreadJumpHintVisibilityController {
  sync: (shouldShow: boolean) => void;
  dispose: () => void;
}

export function createThreadJumpHintVisibilityController(input: {
  delayMs: number;
  onVisibilityChange: (visible: boolean) => void;
  setTimeoutFn?: typeof globalThis.setTimeout;
  clearTimeoutFn?: typeof globalThis.clearTimeout;
}): ThreadJumpHintVisibilityController {
  const setTimeoutFn = input.setTimeoutFn ?? globalThis.setTimeout;
  const clearTimeoutFn = input.clearTimeoutFn ?? globalThis.clearTimeout;
  let isVisible = false;
  let timeoutId: NodeJS.Timeout | null = null;

  const clearPendingShow = () => {
    if (timeoutId === null) {
      return;
    }
    clearTimeoutFn(timeoutId);
    timeoutId = null;
  };

  return {
    sync: (shouldShow) => {
      if (!shouldShow) {
        clearPendingShow();
        if (isVisible) {
          isVisible = false;
          input.onVisibilityChange(false);
        }
        return;
      }

      if (isVisible || timeoutId !== null) {
        return;
      }

      timeoutId = setTimeoutFn(() => {
        timeoutId = null;
        isVisible = true;
        input.onVisibilityChange(true);
      }, input.delayMs);
    },
    dispose: () => {
      clearPendingShow();
    },
  };
}

export function useThreadJumpHintVisibility(): {
  showThreadJumpHints: boolean;
  updateThreadJumpHintsVisibility: (shouldShow: boolean) => void;
} {
  const [showThreadJumpHints, setShowThreadJumpHints] = React.useState(false);
  const controllerRef = React.useRef<ThreadJumpHintVisibilityController | null>(null);

  React.useEffect(() => {
    const controller = createThreadJumpHintVisibilityController({
      delayMs: THREAD_JUMP_HINT_SHOW_DELAY_MS,
      onVisibilityChange: (visible) => {
        setShowThreadJumpHints(visible);
      },
      setTimeoutFn: window.setTimeout.bind(window),
      clearTimeoutFn: window.clearTimeout.bind(window),
    });
    controllerRef.current = controller;

    return () => {
      controller.dispose();
      controllerRef.current = null;
    };
  }, []);

  const updateThreadJumpHintsVisibility = React.useCallback((shouldShow: boolean) => {
    controllerRef.current?.sync(shouldShow);
  }, []);

  return {
    showThreadJumpHints,
    updateThreadJumpHintsVisibility,
  };
}

export function hasUnseenCompletion(thread: ThreadStatusInput): boolean {
  if (!thread.latestTurn?.completedAt) return false;
  const completedAt = Date.parse(thread.latestTurn.completedAt);
  if (Number.isNaN(completedAt)) return false;
  if (!thread.lastVisitedAt) return false;

  const lastVisitedAt = Date.parse(thread.lastVisitedAt);
  if (Number.isNaN(lastVisitedAt)) return true;
  return completedAt > lastVisitedAt;
}

export function shouldClearThreadSelectionOnMouseDown(target: HTMLElement | null): boolean {
  if (target === null) return true;
  return !target.closest(THREAD_SELECTION_SAFE_SELECTOR);
}

// A double-click dispatches two `click` events before `dblclick`: the first has
// `detail === 1`, the second `detail === 2`. The second click must not run the
// row's single-click navigation, otherwise double-click-to-rename would also
// navigate. `MouseEvent.detail` is 0 for synthetic/keyboard activations, which
// still count as a normal single activation.
export function isTrailingDoubleClick(detail: number): boolean {
  return detail > 1;
}

export function resolveSidebarNewThreadEnvMode(input: {
  requestedEnvMode?: SidebarNewThreadEnvMode;
  defaultEnvMode: SidebarNewThreadEnvMode;
}): SidebarNewThreadEnvMode {
  return input.requestedEnvMode ?? input.defaultEnvMode;
}

export function resolveSidebarNewThreadSeedContext(input: {
  projectId: string;
  defaultEnvMode: SidebarNewThreadEnvMode;
  activeThread?: {
    projectId: string;
    branch: string | null;
    worktreePath: string | null;
  } | null;
  activeDraftThread?: {
    projectId: string;
    branch: string | null;
    worktreePath: string | null;
    envMode: SidebarNewThreadEnvMode;
    startFromOrigin: boolean;
  } | null;
}): {
  branch?: string | null;
  worktreePath?: string | null;
  envMode: SidebarNewThreadEnvMode;
  startFromOrigin?: boolean;
} {
  if (input.defaultEnvMode === "worktree") {
    return {
      envMode: "worktree",
    };
  }

  if (input.activeDraftThread?.projectId === input.projectId) {
    return {
      branch: input.activeDraftThread.branch,
      worktreePath: input.activeDraftThread.worktreePath,
      envMode: input.activeDraftThread.envMode,
      startFromOrigin: input.activeDraftThread.startFromOrigin,
    };
  }

  if (input.activeThread?.projectId === input.projectId) {
    return {
      branch: input.activeThread.branch,
      worktreePath: input.activeThread.worktreePath,
      envMode: input.activeThread.worktreePath ? "worktree" : "local",
    };
  }

  return {
    envMode: input.defaultEnvMode,
  };
}

export function orderItemsByPreferredIds<TItem, TId>(input: {
  items: readonly TItem[];
  preferredIds: readonly TId[];
  getId: (item: TItem) => TId;
  getPreferenceIds?: (item: TItem) => readonly TId[];
}): TItem[] {
  const { getId, getPreferenceIds, items, preferredIds } = input;
  if (preferredIds.length === 0) {
    return [...items];
  }

  const indexesByPreferenceId = new Map<TId, number[]>();
  for (const [index, item] of items.entries()) {
    const preferenceIds = getPreferenceIds?.(item) ?? [getId(item)];
    for (const preferenceId of new Set(preferenceIds)) {
      const indexes = indexesByPreferenceId.get(preferenceId);
      if (indexes) {
        indexes.push(index);
      } else {
        indexesByPreferenceId.set(preferenceId, [index]);
      }
    }
  }

  const emittedIndexes = new Set<number>();
  const ordered = preferredIds.flatMap((id) => {
    const index = indexesByPreferenceId
      .get(id)
      ?.find((candidate) => !emittedIndexes.has(candidate));
    if (index === undefined) {
      return [];
    }
    emittedIndexes.add(index);
    return [items[index]!];
  });
  const remaining = items.filter((_, index) => !emittedIndexes.has(index));
  return [...ordered, ...remaining];
}

export function getVisibleSidebarThreadIds<TThreadId>(
  renderedProjects: readonly {
    shouldShowThreadPanel?: boolean;
    renderedThreadIds: readonly TThreadId[];
  }[],
): TThreadId[] {
  return renderedProjects.flatMap((renderedProject) =>
    renderedProject.shouldShowThreadPanel === false ? [] : renderedProject.renderedThreadIds,
  );
}

export function getSidebarThreadIdsToPrewarm<TThreadId>(
  visibleThreadIds: readonly TThreadId[],
  limit = SIDEBAR_THREAD_PREWARM_LIMIT,
): TThreadId[] {
  return visibleThreadIds.slice(0, Math.max(0, limit));
}

export function getSidebarThreadPrewarmPlan<TThreadId>(
  visibleThreadIds: readonly TThreadId[],
  selectedThreadId: TThreadId | null,
): ReadonlyArray<{ readonly threadId: TThreadId; readonly maxDecodedBytes: number }> {
  const threadIds = visibleThreadIds
    .filter((threadId) => threadId !== selectedThreadId)
    .slice(0, SIDEBAR_THREAD_PREWARM_LIMIT);
  if (threadIds.length === 0) return [];

  return threadIds.map((threadId) => ({
    threadId,
    maxDecodedBytes: THREAD_HISTORY_CLIENT_WINDOW_MAX_DECODED_BYTES,
  }));
}

export function supportsBoundedThreadHistoryPrewarm(
  environment:
    | { readonly capabilities: { readonly threadHistory?: string | undefined } }
    | undefined,
): boolean {
  return environment?.capabilities.threadHistory === "bounded-v1";
}

export function resolveAdjacentThreadId<T>(input: {
  threadIds: readonly T[];
  currentThreadId: T | null;
  direction: ThreadTraversalDirection;
}): T | null {
  const { currentThreadId, direction, threadIds } = input;

  if (threadIds.length === 0) {
    return null;
  }

  if (currentThreadId === null) {
    return direction === "previous" ? (threadIds.at(-1) ?? null) : (threadIds[0] ?? null);
  }

  const currentIndex = threadIds.indexOf(currentThreadId);
  if (currentIndex === -1) {
    return null;
  }

  if (direction === "previous") {
    return currentIndex > 0 ? (threadIds[currentIndex - 1] ?? null) : null;
  }

  return currentIndex < threadIds.length - 1 ? (threadIds[currentIndex + 1] ?? null) : null;
}

export function isContextMenuPointerDown(input: {
  button: number;
  ctrlKey: boolean;
  isMac: boolean;
}): boolean {
  if (input.button === 2) return true;
  return input.isMac && input.button === 0 && input.ctrlKey;
}

export function resolveThreadRowClassName(input: {
  isActive: boolean;
  isSelected: boolean;
}): string {
  const baseClassName =
    "h-6 w-full translate-x-0 cursor-pointer justify-start px-2 text-left select-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring sm:h-7";

  // The sidebar's own row states, not `primary` and `accent`. A thread list is
  // long, and a saturated selection repeated down it was the loudest thing on
  // screen; separation here comes from a step in surface, the way it does in
  // the rest of the sidebar.
  if (input.isSelected && input.isActive) {
    return cn(baseClassName, "bg-sidebar-row-active font-medium text-sidebar-foreground");
  }

  if (input.isSelected) {
    return cn(baseClassName, "bg-sidebar-row-selected text-sidebar-foreground");
  }

  if (input.isActive) {
    return cn(baseClassName, "bg-sidebar-row-active font-medium text-sidebar-foreground");
  }

  return cn(
    baseClassName,
    "text-sidebar-foreground hover:bg-sidebar-row-hover hover:text-sidebar-foreground",
  );
}

export function resolveThreadStatusPill(input: {
  thread: ThreadStatusInput;
  suppressPlanReady?: boolean;
}): ThreadStatusPill | null {
  const { thread } = input;

  if (thread.hasPendingApprovals) {
    return {
      label: "Approval",
      colorClass: "text-amber-600 dark:text-amber-300/90",
      dotClass: "bg-amber-500 dark:bg-amber-300/90",
    };
  }

  if (thread.hasPendingUserInput) {
    return {
      label: "Input",
      colorClass: "text-indigo-600 dark:text-indigo-300/90",
      dotClass: "bg-indigo-500 dark:bg-indigo-300/90",
    };
  }

  if (
    thread.session?.status === "running" ||
    thread.session?.status === "starting" ||
    deriveThreadExecutionPhase(thread.latestTurn) === "provider-running"
  ) {
    return {
      label: "Working",
      colorClass: "text-sky-600 dark:text-sky-400",
      dotClass: "bg-sky-500 dark:bg-sky-300/80",
    };
  }

  if (thread.session?.status === "error") {
    return {
      label: "Failed",
      colorClass: "text-red-700 dark:text-red-300",
      dotClass: "bg-red-500 dark:bg-red-300",
    };
  }

  const hasPlanReadyPrompt =
    !input.suppressPlanReady &&
    !thread.hasPendingUserInput &&
    thread.interactionMode === "plan" &&
    isLatestTurnSettled(thread.latestTurn, thread.session) &&
    thread.hasActionableProposedPlan;
  if (hasPlanReadyPrompt) {
    return {
      label: "Plan Ready",
      colorClass: "text-violet-600 dark:text-violet-300/90",
      dotClass: "bg-violet-500 dark:bg-violet-300/90",
    };
  }

  if (hasUnseenCompletion(thread)) {
    return {
      label: "Done",
      colorClass: "text-emerald-600 dark:text-emerald-300/90",
      dotClass: "bg-emerald-500 dark:bg-emerald-300/90",
    };
  }

  return null;
}

export function resolveProjectStatusIndicator(
  statuses: ReadonlyArray<ThreadStatusPill | null>,
): ThreadStatusPill | null {
  let highestPriorityStatus: ThreadStatusPill | null = null;

  for (const status of statuses) {
    if (status === null) continue;
    if (
      highestPriorityStatus === null ||
      THREAD_STATUS_PRIORITY[status.label] > THREAD_STATUS_PRIORITY[highestPriorityStatus.label]
    ) {
      highestPriorityStatus = status;
    }
  }

  return highestPriorityStatus;
}

export function getVisibleThreadsForProject<T extends Pick<Thread, "id">>(input: {
  threads: readonly T[];
  activeThreadId: T["id"] | undefined;
  isThreadListExpanded: boolean;
  previewLimit: number;
}): {
  hasHiddenThreads: boolean;
  visibleThreads: T[];
  hiddenThreads: T[];
} {
  const { activeThreadId, isThreadListExpanded, previewLimit, threads } = input;
  const hasHiddenThreads = threads.length > previewLimit;

  if (!hasHiddenThreads || isThreadListExpanded) {
    return {
      hasHiddenThreads,
      hiddenThreads: [],
      visibleThreads: [...threads],
    };
  }

  const previewThreads = threads.slice(0, previewLimit);
  if (!activeThreadId || previewThreads.some((thread) => thread.id === activeThreadId)) {
    return {
      hasHiddenThreads: true,
      hiddenThreads: threads.slice(previewLimit),
      visibleThreads: previewThreads,
    };
  }

  const activeThread = threads.find((thread) => thread.id === activeThreadId);
  if (!activeThread) {
    return {
      hasHiddenThreads: true,
      hiddenThreads: threads.slice(previewLimit),
      visibleThreads: previewThreads,
    };
  }

  const visibleThreadIds = new Set([...previewThreads, activeThread].map((thread) => thread.id));

  return {
    hasHiddenThreads: true,
    hiddenThreads: threads.filter((thread) => !visibleThreadIds.has(thread.id)),
    visibleThreads: threads.filter((thread) => visibleThreadIds.has(thread.id)),
  };
}

export function getFallbackThreadIdAfterDelete<
  T extends Pick<Thread, "id" | "projectId" | "createdAt" | "updatedAt"> & ThreadSortInput,
>(input: {
  threads: readonly T[];
  deletedThreadId: T["id"];
  sortOrder: SidebarThreadSortOrder;
  deletedThreadIds?: ReadonlySet<T["id"]>;
}): T["id"] | null {
  const { deletedThreadId, deletedThreadIds, sortOrder, threads } = input;
  const deletedThread = threads.find((thread) => thread.id === deletedThreadId);
  if (!deletedThread) {
    return null;
  }

  return (
    sortThreads(
      threads.filter(
        (thread) =>
          thread.projectId === deletedThread.projectId &&
          thread.id !== deletedThreadId &&
          !deletedThreadIds?.has(thread.id),
      ),
      sortOrder,
    )[0]?.id ?? null
  );
}
export function getProjectSortTimestamp(
  project: SidebarProject,
  projectThreads: readonly ThreadSortInput[],
  sortOrder: Exclude<SidebarProjectSortOrder, "manual">,
): number {
  if (projectThreads.length > 0) {
    return projectThreads.reduce(
      (latest, thread) => Math.max(latest, getThreadSortTimestamp(thread, sortOrder)),
      Number.NEGATIVE_INFINITY,
    );
  }

  if (sortOrder === "created_at") {
    return toSortableTimestamp(project.createdAt) ?? Number.NEGATIVE_INFINITY;
  }
  return toSortableTimestamp(project.updatedAt ?? project.createdAt) ?? Number.NEGATIVE_INFINITY;
}

export function sortProjectsForSidebar<
  TProject extends SidebarProject,
  TThread extends Pick<Thread, "projectId" | "createdAt" | "updatedAt"> & ThreadSortInput,
>(
  projects: readonly TProject[],
  threads: readonly TThread[],
  sortOrder: SidebarProjectSortOrder,
): TProject[] {
  if (sortOrder === "manual") {
    return [...projects];
  }

  const threadsByProjectId = new Map<string, TThread[]>();
  for (const thread of threads) {
    const existing = threadsByProjectId.get(thread.projectId) ?? [];
    existing.push(thread);
    threadsByProjectId.set(thread.projectId, existing);
  }

  return [...projects].toSorted((left, right) => {
    const rightTimestamp = getProjectSortTimestamp(
      right,
      threadsByProjectId.get(right.id) ?? [],
      sortOrder,
    );
    const leftTimestamp = getProjectSortTimestamp(
      left,
      threadsByProjectId.get(left.id) ?? [],
      sortOrder,
    );
    const byTimestamp =
      rightTimestamp === leftTimestamp ? 0 : rightTimestamp > leftTimestamp ? 1 : -1;
    if (byTimestamp !== 0) return byTimestamp;
    return left.title.localeCompare(right.title) || left.id.localeCompare(right.id);
  });
}

// ---------------------------------------------------------------------------
// The sidebar's section model.
//
// Rows and the structural markers between them share one sortable list, so a
// drag can cross a section boundary and the markers resolve which lifecycle
// action that implies. Pinned and active threads keep the position they were
// dropped at; settled threads fall back to time order. The snoozed shelf can
// be dragged out of but never into, because snoozing needs a wake time.
// ---------------------------------------------------------------------------

/** Rows only animate between slots while a sort is in progress. */
export const animateSidebarLayoutChanges: AnimateLayoutChanges = (args) =>
  args.isSorting ? defaultAnimateLayoutChanges(args) : false;

export type SidebarSection = "pinned" | "active" | "snoozed" | "settled";

/**
 * Sortable ids. Thread rows use their scoped key, which always contains a
 * colon, so the marker prefix can never collide with one.
 */
const SIDEBAR_MARKER_PREFIX = "sidebar-marker-";

export type SidebarListMarker =
  /** The top boundary, and the landing target when nothing is pinned yet. */
  | "pinned-header"
  /** Stand-ins so an empty section still has somewhere for the gap to open. */
  | "active-placeholder"
  | "settled-placeholder"
  /** The boundary between the pinned block and the Active list. */
  | "pinned-divider"
  | "snoozed-header"
  | "settled-header";

export function sidebarMarkerId(marker: SidebarListMarker): string {
  return `${SIDEBAR_MARKER_PREFIX}${marker}`;
}

export type SidebarListItem =
  | { readonly kind: "thread"; readonly key: string; readonly section: SidebarSection }
  | { readonly kind: "marker"; readonly marker: SidebarListMarker };

export function sidebarListItemId(item: SidebarListItem): string {
  return item.kind === "thread" ? item.key : sidebarMarkerId(item.marker);
}

/**
 * The section a slot belongs to, read off the markers above it: everything
 * before the pinned divider is pinned, then the Active list until the snoozed
 * header, the shelf until the settled header, then settled.
 */
function sectionAtSidebarSlot(items: readonly SidebarListItem[], index: number): SidebarSection {
  let section: SidebarSection = "pinned";
  for (let cursor = 0; cursor < index && cursor < items.length; cursor += 1) {
    const item = items[cursor];
    if (item === undefined || item.kind !== "marker") continue;
    if (item.marker === "pinned-divider") section = "active";
    else if (item.marker === "snoozed-header") section = "snoozed";
    else if (item.marker === "settled-header") section = "settled";
  }
  return section;
}

/** Where a drop lands, and the manual order each affected block ends up in. */
export interface SidebarDropTarget {
  readonly section: "pinned" | "active" | "settled";
  readonly pinnedOrder: readonly string[];
  readonly activeOrder: readonly string[];
}

export function resolveSidebarDropTarget(
  items: readonly SidebarListItem[],
  activeKey: string,
  overId: string,
): SidebarDropTarget | null {
  const activeIndex = items.findIndex((item) => sidebarListItemId(item) === activeKey);
  const overIndex = items.findIndex((item) => sidebarListItemId(item) === overId);
  const dragged = activeIndex === -1 ? undefined : items[activeIndex];
  if (dragged === undefined || overIndex === -1 || dragged.kind !== "thread") return null;
  const moved = items.filter((_, index) => index !== activeIndex);
  moved.splice(overIndex, 0, dragged);
  const section = sectionAtSidebarSlot(moved, overIndex);
  // The shelf is not a destination: snoozing needs a wake time the drag
  // cannot supply.
  if (section === "snoozed") return null;
  const pinnedOrder: string[] = [];
  const activeOrder: string[] = [];
  let currentSection: SidebarSection = "pinned";
  for (const item of moved) {
    if (item.kind === "marker") {
      if (item.marker === "pinned-divider") currentSection = "active";
      else if (item.marker === "snoozed-header" || item.marker === "settled-header") break;
    } else if (currentSection === "pinned") pinnedOrder.push(item.key);
    else activeOrder.push(item.key);
  }
  return { section, pinnedOrder, activeOrder };
}

export type SidebarThreadDropPlan =
  | { readonly kind: "none" }
  /** Within the pinned block: only order keys are written. */
  | {
      readonly kind: "reorder-pinned";
      readonly order: readonly string[];
      readonly assignments: readonly PlacementAssignment[];
    }
  /**
   * Into the pinned block from elsewhere. A fresh pin carries `orderKey` on
   * the pin command itself; `extraAssignments` are written afterwards, and
   * include the moved row when it was already pinned beneath a snooze.
   */
  | {
      readonly kind: "pin";
      readonly order: readonly string[];
      readonly orderKey: string | undefined;
      readonly extraAssignments: readonly PlacementAssignment[];
    }
  | {
      readonly kind: "move-active";
      readonly order: readonly string[];
      readonly assignments: readonly PlacementAssignment[];
      readonly unpin: boolean;
      readonly unsettle: boolean;
      readonly unsnooze: boolean;
    }
  | { readonly kind: "settle" };

/**
 * What dropping in `to` does to a thread lifted from `from`, for the badge on
 * the lifted row. Null while reordering inside one section, and for the
 * snoozed shelf, which cannot be a drop target.
 */
export type SidebarDropVerb = "pin" | "unpin" | "settle" | "unsettle" | "wake";

export function resolveSidebarDropVerb(
  from: SidebarSection,
  to: SidebarSection | null,
): SidebarDropVerb | null {
  if (to === null || to === from || to === "snoozed") return null;
  if (to === "pinned") return "pin";
  if (to === "settled") return "settle";
  if (from === "pinned") return "unpin";
  if (from === "settled") return "unsettle";
  return "wake";
}

export function planSidebarThreadDrop(input: {
  readonly activeKey: string;
  readonly activeSection: SidebarSection;
  /** A snoozed thread can still be pinned or settled beneath the shelf. */
  readonly activePinned?: boolean;
  readonly activeSettled?: boolean;
  readonly target: SidebarDropTarget;
  /** Every pinned key in displayed order, before the drop. */
  readonly pinnedOrder: readonly string[];
  readonly pinnedKeysById: ReadonlyMap<string, string | null | undefined>;
  readonly reorderableKeys?: ReadonlySet<string>;
  readonly activeOrder: readonly string[];
  readonly activeKeysById: ReadonlyMap<string, string | null | undefined>;
  readonly activeReorderableKeys?: ReadonlySet<string>;
}): SidebarThreadDropPlan {
  const {
    activeKey,
    activeSection,
    activePinned = activeSection === "pinned",
    activeSettled = activeSection === "settled",
    target,
    pinnedOrder,
    pinnedKeysById,
    reorderableKeys,
    activeOrder,
    activeKeysById,
    activeReorderableKeys,
  } = input;
  const sameOrder = (next: readonly string[], previous: readonly string[]) =>
    next.length === previous.length && next.every((key, index) => key === previous[index]);

  switch (target.section) {
    case "active": {
      const order = target.activeOrder;
      if (activeSection === "active" && sameOrder(order, activeOrder)) return { kind: "none" };
      const assignments = planPlacementReorder({
        orderedIds: order,
        keysById: activeKeysById,
        movedId: activeKey,
      });
      // An AFAR Environment that cannot persist the order must not be sent a
      // partial rewrite of it.
      if (activeReorderableKeys && assignments.some(({ id }) => !activeReorderableKeys.has(id))) {
        return { kind: "none" };
      }
      return {
        kind: "move-active",
        order,
        assignments,
        unpin: activePinned,
        unsettle: activeSettled,
        unsnooze: activeSection === "snoozed",
      };
    }
    case "settled":
      return activeSection === "settled" ? { kind: "none" } : { kind: "settle" };
    case "pinned": {
      const order = target.pinnedOrder;
      if (activeSection === "pinned" && sameOrder(order, pinnedOrder)) return { kind: "none" };
      const assignments = planPlacementReorder({
        orderedIds: order,
        keysById: pinnedKeysById,
        movedId: activeKey,
      });
      if (reorderableKeys && assignments.some(({ id }) => !reorderableKeys.has(id))) {
        return { kind: "none" };
      }
      if (activeSection === "pinned") {
        return assignments.length === 0
          ? { kind: "none" }
          : { kind: "reorder-pinned", order, assignments };
      }
      return {
        kind: "pin",
        order,
        orderKey: assignments.find((assignment) => assignment.id === activeKey)?.orderKey,
        extraAssignments: activePinned
          ? assignments
          : assignments.filter((assignment) => assignment.id !== activeKey),
      };
    }
  }
}

/**
 * The thread fields a dropped row is previewed through while its commands are
 * still in flight. Projecting the same lifecycle rules the Environment applies
 * keeps the row where it was released once the events arrive.
 */
export interface SidebarDroppedThread {
  readonly management?: ThreadManagement | null | undefined;
  readonly placement?: ThreadPlacement | null | undefined;
  readonly unsettledAt?: string | null | undefined;
}

export function applySidebarThreadDrop<T extends SidebarDroppedThread>(
  thread: T,
  section: "pinned" | "active" | "settled",
  now: string,
  orderKey?: string,
): T {
  const wasSettled = thread.management?.settledAt != null;
  const placement = thread.placement ?? emptyThreadPlacement;
  if (section === "settled") {
    return {
      ...thread,
      management: {
        settledAt: wasSettled ? thread.management!.settledAt : now,
        snoozedAt: null,
        snoozedUntil: null,
      },
      placement: { ...emptyThreadPlacement, activeOrderKey: placement.activeOrderKey },
      unsettledAt: null,
    };
  }
  return {
    ...thread,
    // Dropping anywhere but the shelf wakes the thread and clears a settle.
    management: null,
    ...(wasSettled ? { unsettledAt: now } : {}),
    placement: {
      pinnedAt: section === "pinned" ? (placement.pinnedAt ?? now) : null,
      pinOrderKey: section === "pinned" ? (orderKey ?? placement.pinOrderKey) : null,
      activeOrderKey:
        section === "active" && orderKey !== undefined ? orderKey : placement.activeOrderKey,
    },
  };
}

/**
 * Filters the sidebar's own list down to a query, matching what a row already
 * shows: its title and its branch. An empty query matches nothing rather than
 * everything — the caller renders the sectioned list instead of results.
 */
export function searchSidebarThreads<
  T extends { readonly title: string; readonly branch?: string | null },
>(threads: readonly T[], query: string): T[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery.length === 0) return [];
  return threads.filter((thread) =>
    [thread.title, thread.branch ?? ""].some((term) =>
      term.toLowerCase().includes(normalizedQuery),
    ),
  );
}
